import { BaseEffect } from '../BaseEffect.js';

/**
 * CRT screen effect implementation
 */
export class CRTEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            curvature: 0.5,
            cornerDarkness: 0.3,
            scanlineDensity: 100,
            scanlineSpeed: 1.0,
            phosphorGlow: 0.4,
            colorBleeding: 0.3,
            flickerIntensity: 0.02,
            animate: true
        };

        super('crt', shaderManager, defaultParams);
    }

    /**
     * Initialize the effect
     */
    async init() {
        await super.init();  // This will load crt.vert and crt.frag from /public/shaders/
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_curvature', { type: '1f' });
        this.addUniform('u_cornerDarkness', { type: '1f' });
        this.addUniform('u_scanlineDensity', { type: '1f' });
        this.addUniform('u_scanlineSpeed', { type: '1f' });
        this.addUniform('u_phosphorGlow', { type: '1f' });
        this.addUniform('u_colorBleeding', { type: '1f' });
        this.addUniform('u_flickerIntensity', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        Object.assign(uniforms, {
            u_curvature: { value: this.params.curvature, type: '1f' },
            u_cornerDarkness: { value: this.params.cornerDarkness, type: '1f' },
            u_scanlineDensity: { value: this.params.scanlineDensity, type: '1f' },
            u_scanlineSpeed: { value: this.params.scanlineSpeed, type: '1f' },
            u_phosphorGlow: { value: this.params.phosphorGlow, type: '1f' },
            u_colorBleeding: { value: this.params.colorBleeding, type: '1f' },
            u_flickerIntensity: { value: this.params.flickerIntensity, type: '1f' }
        });

        // If animation is disabled, use 0 for time to stop flicker
        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            curvature: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Screen Curvature'
            },
            cornerDarkness: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Corner Darkness'
            },
            scanlineDensity: {
                min: 50,
                max: 200,
                step: 1,
                label: 'Scanline Density'
            },
            scanlineSpeed: {
                min: 0,
                max: 5,
                step: 0.1,
                label: 'Scanline Speed'
            },
            phosphorGlow: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Phosphor Glow'
            },
            colorBleeding: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Color Bleeding'
            },
            flickerIntensity: {
                min: 0,
                max: 0.1,
                step: 0.001,
                label: 'Flicker Intensity'
            },
            animate: {
                label: 'Animate Effects'
            }
        };
    }
} 